import { Popover, notification } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuditEventSearchResult } from '../api/models';
import APIClient from '../api/api';
import DateRangeFilter, { defaultQuickRanges } from '../components/DateRangeFilter';
import dayjs from 'dayjs';
import { formatDateTime } from '../utils';
import {
  Button,
  Column,
  DataTable,
  DomainName,
  FilterPill,
  Pagination,
} from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';

const DEFAULT_PAGE_SIZE = 25;

const eventTypeOptions = [
  { value: '', label: 'All Event Types' },
  { value: 'domainAdded', label: 'Domain Added' },
  { value: 'domainDeleted', label: 'Domain Deleted' },
  { value: 'domainRenewed', label: 'Domain Renewed' },
  { value: 'domainUserRenew', label: 'Domain User Renew' },
  { value: 'domainReactivated', label: 'Domain Reactivated' },
  { value: 'domainExpired', label: 'Domain Expired' },
  { value: 'domainDelisted', label: 'Domain Delisted' },
  { value: 'domainAutoPicked', label: 'Domain Auto Picked' },
  { value: 'domainAutoPickedLegacy', label: 'Domain Auto Picked (Legacy)' },
  { value: 'domainPickEmailSent', label: 'Pick Email Sent' },
  { value: 'domainPickSpaceshipEmailSent', label: 'Pick Spaceship Email Sent' },
  { value: 'domainInternalRenewalEmailSent', label: 'Internal Renewal Email Sent' },
  { value: 'domainPickSupportReactivated', label: 'Pick Support Reactivated' },
  { value: 'domainPickSupportIneligible', label: 'Pick Support Ineligible' },
  { value: 'portfolioDomainRenewed', label: 'Portfolio Domain Renewed' },
  { value: 'portfolioDomainDeleted', label: 'Portfolio Domain Deleted' },
  { value: 'portfolioDomainRenewalLegacy', label: 'Portfolio Renewal (Legacy)' },
  { value: 'portfolioDomainRenewalListPick', label: 'Portfolio Renewal List Pick' },
  { value: 'manualPick', label: 'Manual Pick' },
  { value: 'pickRemoved', label: 'Pick Removed' },
  { value: 'prePick', label: 'Pre-Pick' },
  { value: 'listingAdded', label: 'Listing Added' },
  { value: 'listingUpdated', label: 'Listing Updated' },
  { value: 'listingDeleted', label: 'Listing Deleted' },
  { value: 'autoRenewEnabled', label: 'Auto-Renew Enabled' },
  { value: 'autoRenewDisabled', label: 'Auto-Renew Disabled' },
  { value: 'domainAutoRenewChanged', label: 'Auto-Renew Changed' },
  { value: 'alertCreated', label: 'Alert Created' },
  { value: 'alertReFired', label: 'Alert Re-Fired' },
  { value: 'alertNotesUpdated', label: 'Alert Notes Updated' },
  { value: 'alertResolved', label: 'Alert Resolved' },
  { value: 'alertDismissed', label: 'Alert Dismissed' },
  { value: 'pricingUpdated', label: 'Pricing Updated' },
  { value: 'priceReduction', label: 'Price Reduction' },
  { value: 'listingPriceReconciled', label: 'Listing Price Reconciled' },
  { value: 'domainTagged', label: 'Domain Tagged' },
  { value: 'domainUntagged', label: 'Domain Untagged' },
  { value: 'metricsScan', label: 'Metrics Scan' },
  { value: 'scheduledScan', label: 'Scheduled Scan' },
  { value: 'salesActivityManuallyAdded', label: 'Sales Activity Added' },
  { value: 'afternicListingFailureEmailSent', label: 'Afternic Listing Failure Email' },
  { value: 'removeDeprecatedRulePick', label: 'Remove Deprecated Rule Pick' },
  { value: 'ruleDeactivated', label: 'Rule Deactivated' },
];

const AuditLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<AuditEventSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [domainInputValue, setDomainInputValue] = useState('');

  const domainFilter = searchParams.get('domain') || '';
  const eventTypeFilter = searchParams.get('eventType') || '';
  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  const quickRangeParam = searchParams.get('range');
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    setDomainInputValue(domainFilter);
  }, [domainFilter]);

  const dateRange = useMemo((): [dayjs.Dayjs, dayjs.Dayjs] | null => {
    if (quickRangeParam) {
      const quickRange = defaultQuickRanges.find((r) => r.value === quickRangeParam);
      if (quickRange) return quickRange.range;
    }
    if (startDateParam && endDateParam) {
      return [dayjs(startDateParam), dayjs(endDateParam)];
    }
    return null;
  }, [quickRangeParam, startDateParam, endDateParam]);

  const selectedQuickRange = quickRangeParam || null;

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  function handleDomainFilterChange(value: string) {
    updateSearchParams({ domain: value || null, page: '1' });
  }

  function handleEventTypeFilterChange(value: string) {
    updateSearchParams({ eventType: value || null, page: '1' });
  }

  function handleDateRangeChange(range: [dayjs.Dayjs, dayjs.Dayjs] | null) {
    if (range) {
      updateSearchParams({
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD'),
        range: null,
        page: '1',
      });
    } else {
      updateSearchParams({ startDate: null, endDate: null, range: null, page: '1' });
    }
  }

  function handleQuickRangeChange(value: string | null) {
    if (value) {
      updateSearchParams({ range: value, startDate: null, endDate: null, page: '1' });
    }
  }

  const requestIdRef = useRef(0);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;

    async function loadAuditEvents() {
      try {
        setLoading(true);

        const eventTypes = eventTypeFilter ? [eventTypeFilter] : ['all'];

        const resp = await APIClient.searchAuditEvents({
          startDate: dateRange ? dateRange[0].toDate() : undefined,
          endDate: dateRange ? dateRange[1].toDate() : undefined,
          eventTypes,
          page: pageParam,
          pageSize: pageSize,
          domain: domainFilter || undefined,
        });

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setData(resp.content);

        let computedTotal = resp.total;
        if (resp.total === -1) {
          const hasMore = resp.totalPages > resp.currentPage;
          computedTotal = resp.currentPage * resp.pageSize + (hasMore ? 1 : 0);
        }

        setTotal(computedTotal);
      } catch (e) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        console.error(e);
        notification.error({ message: 'Failed to load audit logs.' });
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }

    loadAuditEvents();
  }, [dateRange, domainFilter, eventTypeFilter, pageParam, pageSize]);

  function handleExport() {
    const eventTypes = eventTypeFilter ? [eventTypeFilter] : ['all'];
    const url = APIClient.getAuditEventsExportUrl({
      startDate: dateRange ? dateRange[0].toDate() : undefined,
      endDate: dateRange ? dateRange[1].toDate() : undefined,
      eventTypes,
      domain: domainFilter || undefined,
    });
    window.open(url, '_blank');
  }

  function setPage(p: number) {
    updateSearchParams({ page: String(p) });
  }

  const eventTypeLabel =
    eventTypeOptions.find((o) => o.value === eventTypeFilter)?.label || 'All Event Types';

  const dateLabel = selectedQuickRange
    ? selectedQuickRange.replace(/-/g, ' ')
    : dateRange
    ? `${dateRange[0].format('MMM D')} – ${dateRange[1].format('MMM D')}`
    : 'Any';

  const columns: Column<AuditEventSearchResult>[] = [
    {
      key: 'domain',
      title: 'Domain',
      render: (record) => <DomainName name={record.domain} />,
    },
    {
      key: 'auditEventType',
      title: 'Event Type',
      width: 220,
      render: (record) => (
        <span className="text-text-2 text-[12px] font-mono">{record.auditEventType}</span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Date',
      width: 160,
      render: (record) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {formatDateTime(record.createdAt)}
        </span>
      ),
    },
    {
      key: 'eventSource',
      title: 'Source',
      width: 140,
      render: (record) => (
        <span className="text-text-3 text-[11.5px]">{record.eventSource || '—'}</span>
      ),
    },
    {
      key: 'data',
      title: 'Data',
      render: (record) =>
        record.data && record.data.length > 0 ? (
          <span className="text-text-3 text-[11px] font-mono break-all line-clamp-2 max-w-[400px]">
            {record.data}
          </span>
        ) : (
          <span className="text-text-3">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHead
        title="Audit Logs"
        subtitle="Searchable history of every event recorded by the system."
        actions={
          <Button variant="ghost" iconLeft={<DownloadOutlined />} onClick={handleExport}>
            Export CSV
          </Button>
        }
      />
      <PageToolbar>
        <Popover
          trigger="click"
          placement="bottomLeft"
          content={
            <div className="p-2 min-w-[280px]">
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                selectedQuickRange={selectedQuickRange}
                onQuickRangeChange={handleQuickRangeChange}
                allowEmpty
              />
            </div>
          }
        >
          <span>
            <FilterPill label="Date" value={dateLabel} active={!!dateRange} />
          </span>
        </Popover>

        <Popover
          trigger="click"
          placement="bottomLeft"
          content={
            <div className="p-1 flex flex-col min-w-[260px] max-h-[400px] overflow-auto">
              {eventTypeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleEventTypeFilterChange(opt.value)}
                  className={`text-left px-2 py-1.5 text-[12.5px] rounded hover:bg-bg-elevated ${
                    eventTypeFilter === opt.value ? 'text-accent font-semibold' : 'text-text-2'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          }
        >
          <span>
            <FilterPill label="Event" value={eventTypeLabel} active={!!eventTypeFilter} />
          </span>
        </Popover>

        <input
          type="text"
          value={domainInputValue}
          placeholder="Search domain…"
          onChange={(e) => {
            setDomainInputValue(e.target.value);
            if (!e.target.value) handleDomainFilterChange('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleDomainFilterChange(domainInputValue);
          }}
          className="bg-bg-elevated border border-transparent rounded-md px-3 py-1 w-[260px] text-[12.5px] text-text outline-none focus:border-border-strong placeholder:text-text-3"
        />

        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {data.length > 0 ? (
            <>
              showing <b className="text-text-2">{data.length}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={data}
        rowKey={(r, i) => `${r.domain}-${r.createdAt}-${r.auditEventType}-${i}`}
        loading={loading}
        emptyMessage="No audit events match these filters."
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{data.length}</b>
          </span>
        }
        right={
          <Pagination
            page={pageParam}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            loading={loading}
          />
        }
      />
    </>
  );
};

export default AuditLogs;
